/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { FancyFirebaseObject } from '../base/FancyFirebaseObject';
import { AngularLandConnection, RefOrObservable } from '../base/interfaces';
import { mergeMap } from 'rxjs/operators';
import { FancyAction } from '../base/FancyAction';
import { EMPTY, Observable, of } from 'rxjs';
import { DefaultActionHandlers } from '../generics/DefaultActionHandlers';
import { SplitData } from '@touchplan/common';
import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';

/**
 * Simplified version of [[FancyFirebaseSplitDataList]] for objects. Supports a subset of the actions.
 * And like FancyFirebaseObject itself there's no support for listening to actions/ action types.
 */
export abstract class FancyFirebaseSplitDataObject<
	TModelFields extends object,
	TModelRenderTypes extends string,
	TModel extends SplitData<TModelFields, TModelRenderTypes>
> extends FancyFirebaseObject<TModel> {
	protected defaultActionsHandlers: DefaultActionHandlers<TModel, FancyAction, this> =
		new DefaultActionHandlers(this);
	/**
	 * ...creates the model
	 * @param angularLand - Connection object that interfaces with AngularFireDatabase
	 * @param path - Reference or Observable pointing to the Firebase database location
	 */
	constructor(angularLand: AngularLandConnection, path: RefOrObservable) {
		super(angularLand, path);
	}

	/** return an instance of the model class. Will be explictly triggered when swapping from null -> not null */
	protected abstract splitDataFactory(child: DatabaseSnapshot<TModelFields>): TModel;

	/**
	 * Standardized update handling, handles null data, creating the model as needed and
	 * applying the updated data to the split data inheritor.
	 * @param child - Angularfire event wrapped firebase snapshot containing updated data
	 * @returns A populated model or null if data doesn't exist
	 */
	public $$updated(child: AngularFireAction<DatabaseSnapshot<TModelFields>>): null | TModel {
		if (!child || !child.payload.exists()) {
			return null;
		}
		let model!: TModel;

		//(internalObject is _intended to be used internally, it's deprecated for _external_ use)
		if (this._internalObject) {
			model = this._internalObject;
		} else {
			model = this.splitDataFactory(child.payload);
		}

		// const model: ActiveLine = new ActiveLine(child.payload.key);
		model.databaseUpdate(child.payload.val());
		return model;
	}

	public handleCustomEvents(
		baseObservable: Observable<FancyAction<TModel>>
	): Observable<FancyAction<TModel>> {
		return baseObservable.pipe(
			mergeMap((action: FancyAction) => {
				const item = this.defaultActionsHandlers.handle(action);
				if (!item) {
					return EMPTY;
				} else if (item instanceof Observable) {
					return item;
				}
				return of(new FancyAction(item, action.type));
			})
		);
	}
}
